<template>
  <VRow justify="center">
    <VDialog
        :value="visible"
        scrollable
        max-width="500px"
        @click:outside="$emit('close')"
    >
      <VCard
          tile
          :loading="isLoading"
      >
        <VCardTitle class="justify-space-between">
          <h6 class="heading-6">
            {{ $t("trips.cancellationDialogTitle") }}
          </h6>
          <VBtn
            class="box-shadow-elevation-6 close-card-btn"
            icon
            data-test="close-cancellation-dialog"
            @click.stop="$emit('close')"
          >
            <VIcon color="textPrimary"> mdi-close </VIcon>
          </VBtn>
        </VCardTitle>
        <VCardText class="body-1 textPrimary--text">
          {{ $t("trips.cancellationDialogText") }}
          <br />
          <template
            v-if="
              cancellationPenalty !== null &&
              cancellationPenalty !== 0 &&
              bookingPrice !== 0
            "
          >
            {{
              $t("trips.cancellationDialogPenaltyText", {
                cancellationPenaltyPrice: getCancellationPenaltyPrice,
              })
            }}
            ({{ cancellationPenalty }} %)
          </template>
        </VCardText>
        <VCardActions
          class="pb-4"
          :class="isMobile ? 'd-flex flex-column' : ''"
        >
          <div 
            class="px-1"
            :class="isMobile ? 'w-100 pb-2' : 'w-50'"
          >
            <VBtn
              class="w-100 border-radius-8"
              outlined
              data-test="keep-trip-button"
              @click.stop="$emit('close')"
            >
              <span class="button">
                {{ $t("trips.keepTrip") }}
              </span>
            </VBtn>
          </div>
          <div 
            class="px-1"
            :class="isMobile ? 'w-100 pb-2' : 'w-50'"
          >
            <VBtn
              class="w-100 text-decoration-none border-radius-8"
              color="error"
              :data-test="`trip-${bookingId}-cancelButton`"
              :disabled="isLoading"
              @click="cancelBooking"
            >
              <span class="button-icon mr-2">
                <VIcon size="large"> mdi-check </VIcon>
              </span>
              <span class="button">
                {{ $t("trips.cancelTrip") }}
              </span>
            </VBtn>
          </div>
        </VCardActions>
      </VCard>
    </VDialog>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'CancellationDialog',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import SBookingsService from '@slg/web-customer-shared/src/services/BookingsService';
import { useGlobalErrorDialog, useVuetify } from '../../composables';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
    default: false,
  },
  bookingId: {
    type: String,
    required: true,
  },
  bookingPrice: {
    type: Number,
    required: 0,
  },
});

const emit = defineEmits(['close', 'onCancelled']);

const { setGlobalError } = useGlobalErrorDialog();
const vuetify = useVuetify();

const isLoading = ref(false);
const cancellationPenalty = ref(null);
const cancellationPenaltyPrice = ref(null);

const getCancellationPenaltyPrice = computed(() => (cancellationPenaltyPrice.value ? (+cancellationPenaltyPrice.value).toFixed(2) : ''));

const isMobile = computed(() => vuetify.breakpoint.xs);

watch(() => props.visible, (value) => {
  if (value) {
    getCancellationPenalty();
  } else {
    cancellationPenalty.value = null;
  }
});

async function getCancellationPenalty() {
  isLoading.value = true;
  try {
    const result = await SBookingsService.getCancellationPenalty(
      props.bookingId
    );
    cancellationPenalty.value = result?.data?.cancellationFee ?? null;
    cancellationPenaltyPrice.value =
      props.bookingPrice * (result?.data?.cancellationFee / 100);
  } catch (error) {
    if (e.status !== 401) {
      await setGlobalError(error);
    }
  }
  isLoading.value = false;
}

async function cancelBooking() {
  isLoading.value = true;
  try {
    await SBookingsService.cancelBooking(props.bookingId);
    emit('onCancelled');
  } catch (e) {
    if (e.status !== 401) {
      await setGlobalError(e, false);
    }
  }
  emit('close');
  isLoading.value = false;
}
</script>

<style scoped lang="scss">
</style>
